import type { ReactNode } from 'react';
import React from 'react';
import { Accordion } from 'is24-corecss';
import { messages } from '../../../../../i18n/messages';
import { useIntl } from '@/i18n/hooks/useIntl';
import { default as Msg } from '@/i18n/components/FormattedMessage';
import './FAQList.css';
import type { FormattedMessageDictionary } from '@/@types/FormattedMessageValue';

const styles = {
  faqList: { maxWidth: 530 },
};

type FAQListProps = {
  benefit: keyof (typeof messages)['de-DE']['benefits'];
  start?: number;
  end?: number;
  values?: FormattedMessageDictionary;
};

export const FAQList = ({ benefit, start, end, values = {} }: FAQListProps) => {
  const { t } = useIntl();
  const benefitDefinition = messages['de-DE'].benefits[benefit];
  if (!('modal' in benefitDefinition) || !('faq' in benefitDefinition.modal)) return null;

  const benefitFaqs = benefitDefinition.modal.faq as Record<string, { redirectionUrl?: string }>;
  const numberOfQuestions = Object.entries(benefitFaqs).length;
  const startIndex = start || 0;
  const endIndex = end || numberOfQuestions - 1;

  return (
    <div style={styles.faqList}>
      <Accordion.Root className='Accordion margin-bottom-xl'>
        {[...Array(endIndex - startIndex + 1)].map((element, index) => {
          const messageNumber = startIndex + index;
          return (
            <Accordion.Item key={index}>
              <Accordion.Header>
                {t({
                  id: `benefits.${benefit}.modal.faq.item${messageNumber}.question`,
                  defaultMessage: `Question ${messageNumber}`,
                })}
              </Accordion.Header>
              <Accordion.Content>
                <Msg
                  id={`benefits.${benefit}.modal.faq.item${messageNumber}.answer`}
                  defaultMessage={`Answer ${messageNumber}`}
                  values={{
                    ...{
                      br: <br />,
                      ul: (children: ReactNode[]) => <ul className='list-bullet'>{children}</ul>,
                      li: (children: ReactNode[]) => <li>{children}</li>,
                      strong: (children: ReactNode[]) => <strong>{children}</strong>,
                      p: (children: ReactNode[]) => <p>{children}</p>,
                      a: (children: ReactNode[]) => {
                        const redirectionUrl = benefitFaqs[`item${messageNumber}`]?.redirectionUrl;
                        return (
                          <a href={redirectionUrl} target='_blank' rel='noreferrer'>
                            {children}
                          </a>
                        );
                      },
                    },
                    ...values,
                  }}
                />
              </Accordion.Content>
            </Accordion.Item>
          );
        })}
      </Accordion.Root>
    </div>
  );
};
